import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import Config from "../../Config";

import PrimaryButton from "../../components/Buttons/PrimaryButton";
import SecondaryButton from "../../components/Buttons/SecondaryButton";

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const { login, loginWithKeycloak, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const emailVerified = sessionStorage.getItem("emailVerified");
    if (emailVerified === "true") {
      setSuccessMessage(
        "Email verified successfully! Please sign in to continue."
      );
      sessionStorage.removeItem("emailVerified");
    }
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const pendingOrgSetupToken = sessionStorage.getItem("pendingOrgSetupToken");
      const pendingPlan = localStorage.getItem("pendingPlan");
      if (pendingOrgSetupToken) {
        navigate(`/organization/setup?token=${pendingOrgSetupToken}`, { replace: true });
      } else if (pendingPlan === "individual") {
        navigate("/card-details", { replace: true });
      } else {
        navigate("/home/dashboard", { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleKeycloakLogin = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithKeycloak();
    } catch (error: any) {
      console.error("Keycloak login error:", error);
      setError("Failed to initiate PSDI login. Please try again.");
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      await login(email, password);
      // Check if there's a pending organization setup
      const pendingOrgSetupToken = sessionStorage.getItem("pendingOrgSetupToken");
      const pendingPlan = localStorage.getItem("pendingPlan");
      if (pendingOrgSetupToken) {
        navigate(`/organization/setup?token=${pendingOrgSetupToken}`);
      } else if (pendingPlan === "individual") {
        navigate("/card-details");
      } else {
        navigate("/home/dashboard");
      }
    } catch (error: any) {
      console.error("Login error:", error);

      if (error.code) {
        switch (error.code) {
          case "auth/user-not-found":
          case "auth/wrong-password":
          case "auth/invalid-credential":
            setError("Incorrect email or password");
            break;
          case "auth/too-many-requests":
            setError("Too many attempts. Please try again later.");
            break;
          case "auth/user-disabled":
            setError("This account has been disabled.");
            break;
          default:
            setError(error.message || "Login failed");
        }
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50">
      <div className="w-full max-w-md px-4">
        <div className="text-left">
          <h2 className="text-3xl font-semibold mb-8 text-left">
            Login to your account
          </h2>

          <div className="max-w-md mx-auto">
            {Config.KEYCLOAK_ENABLED && (
              <>
                <button
                  onClick={handleKeycloakLogin}
                  disabled={loading}
                  className="flex justify-center items-center border border-gray-300 rounded shadow-sm p-3 w-full bg-white hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <img
                    src="./psdi-icon.png"
                    alt="PSDI logo"
                    className="mr-2"
                    width={25}
                  />
                  <span>
                    <strong className="text-sm">Login with PSDI</strong>
                  </span>
                </button>

                <div className="flex items-center my-6">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="mx-4 text-gray-500">or</span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>
              </>
            )}

            <form onSubmit={handleLogin} className="mt-6">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <PrimaryButton
                className="mt-4 w-full"
                type="submit"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login"}
              </PrimaryButton>

              {successMessage && (
                <div
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 mt-4"
                  role="alert"
                >
                  {successMessage}
                </div>
              )}

              {error && (
                <div
                  className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mt-4"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <div className="mt-3 text-center">
                <Link
                  to="/forgotPassword"
                  className="text-[#1d365d] hover:underline"
                >
                  I forgot my password
                </Link>
              </div>
            </form>
            <hr className="my-6 border-gray-300" />

            <h2 className="text-2xl font-semibold mt-8 text-center">
              Don't have an account yet?
            </h2>
            <p className="mt-4 text-gray-600 text-center">
              Unleash the full potential of your data. Join DataRevival to
              transform information into intelligent insight.
            </p>
            <div className="text-center">
              <SecondaryButton
                className="mt-5 w-full"
                onClick={() => navigate("/signup")}
              >
                Sign up
              </SecondaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
